import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';

import { AlertMessageComponent } from 'app/components/ui-kit/alerts/alert-message/alert-message.component';
import { PrimaryBtnComponent } from 'app/components/ui-kit/buttons/primary-btn/primary-btn.component';
import { SecondaryBtnComponent } from 'app/components/ui-kit/buttons/secondary-btn/secondary-btn.component';
import { BaseInputComponent } from 'app/components/ui-kit/inputs/base-input/base-input.component';
import { PhoneInputComponent, PhoneValue } from 'app/components/ui-kit/inputs/phone-input/phone-input.component';
import { SpinnerComponent } from 'app/components/ui-kit/loaders/spinner/spinner.component';
import { PageWrapperComponent } from 'app/components/ui-kit/page-components/page-wrapper/page-wrapper.component';
import { PopTrayComponent } from 'app/components/ui-kit/popovers/pop-tray/pop-tray.component';
import { HeavyHeaderComponent } from 'app/components/ui-kit/typography/heavy-header/heavy-header.component';
import { InviteService, TrustedContact } from 'app/services/invite.service';
import { UserService } from 'app/services/user.service';
import { filter, take } from 'rxjs';

type TrayState = 'form' | 'success' | 'error';

@Component({
  selector: 'app-trusted-contacts',
  standalone: true,
  imports: [
    AlertMessageComponent,
    BaseInputComponent,
    CommonModule,
    FormsModule,
    HeavyHeaderComponent,
    PageWrapperComponent,
    PhoneInputComponent,
    PopTrayComponent,
    PrimaryBtnComponent,
    SecondaryBtnComponent,
    SpinnerComponent
  ],
  templateUrl: './trusted-contacts.component.html',
  styleUrl: './trusted-contacts.component.scss'
})
export class TrustedContactsComponent {
  contacts: TrustedContact[] = [];
  maxContacts = 10;
  isLoading = true;
  isSoftVerified = false;
  listError: string | null = null;

  showTray = false;
  trayState: TrayState = 'form';
  contactName = '';
  contactPhone: PhoneValue | string | null = '';
  contactPhoneE164: string | null = null;
  isContactPhoneValid = false;
  trayError: string | null = null;
  createdContact: TrustedContact | null = null;
  contactToDelete: TrustedContact | null = null;
  isSubmitting = false;
  isDeleting = false;
  copiedKey: string | null = null;

  trayActions = {
    closeFunction: () => this.closeTray()
  };

  deleteContactActions = {
    primaryButtons: [
      {
        text: 'Delete Contact',
        function: () => this.confirmDeleteContact(),
      }
    ],
    secondaryButtons: [
      {
        text: 'Cancel',
        function: () => this.closeDeleteTray(),
      }
    ],
    closeFunction: () => this.closeDeleteTray()
  };

  constructor(
    private inviteService: InviteService,
    private userService: UserService
  ) {}

  ngOnInit() {
    this.userService.userProfile$
      .pipe(
        filter(profile => !!profile),
        take(1)
      )
      .subscribe(profile => {
        this.isSoftVerified = this.isSoftVerifiedProfile(profile);

        if (this.isSoftVerified) {
          this.isLoading = false;
          return;
        }

        this.loadContacts();
      });
  }

  get contactCount(): number {
    return this.contacts.length;
  }

  get canAddContact(): boolean {
    return this.contactCount < this.maxContacts;
  }

  get trayDescription(): string {
    if (this.trayState === 'success') {
      return 'Send this message to your trusted contact.';
    }

    if (this.trayState === 'error') {
      return 'Something went wrong while creating this trusted contact.';
    }

    return 'Add the name and mobile number for the contact you trust.';
  }

  get trayHeader(): string {
    return this.trayState === 'success' ? 'Success' : 'Add Contact';
  }

  get createdContactMessage(): string {
    if (!this.createdContact) {
      return '';
    }

    return this.buildShareMessage(this.createdContact);
  }

  get deleteContactDescription(): string {
    if (!this.contactToDelete) {
      return '';
    }

    return `Delete the invite for ${this.contactToDelete.name || 'this trusted contact'}? This will invalidate code ${this.contactToDelete.token}.`;
  }

  loadContacts() {
    this.isLoading = true;
    this.listError = null;

    this.inviteService.listTrustedContacts().subscribe({
      next: response => {
        this.contacts = response.contacts || [];
        this.maxContacts = response.max || 10;
        this.isLoading = false;
      },
      error: err => {
        this.listError = err.error?.error || 'Unable to load trusted contacts.';
        this.isLoading = false;
      }
    });
  }

  openTray() {
    if (this.isSoftVerified) {
      return;
    }

    if (!this.canAddContact) {
      this.listError = 'You have reached the trusted contact limit.';
      return;
    }

    this.resetTray();
    this.showTray = true;
  }

  closeTray() {
    this.showTray = false;
    this.resetTray();
  }

  submitContact(form: NgForm) {
    this.trayError = null;
    const normalizedPhone = this.normalizedContactPhone();
    if (form.invalid || !this.contactName.trim() || !normalizedPhone || this.isSubmitting) {
      if (!normalizedPhone) {
        this.trayError = 'Enter a valid US mobile number.';
      }
      return;
    }

    this.isSubmitting = true;
    this.inviteService.createInvite(normalizedPhone, this.contactName).subscribe({
      next: response => {
        this.createdContact = response.contact;
        this.contacts = [
          response.contact,
          ...this.contacts.filter(contact => contact.id !== response.contact.id)
        ];
        this.trayState = 'success';
        this.isSubmitting = false;
      },
      error: err => {
        this.trayError = err.error?.error || 'Failed to add trusted contact.';
        this.trayState = 'error';
        this.isSubmitting = false;
      }
    });
  }

  tryAgain() {
    this.resetTray();
  }

  addAnother() {
    this.resetTray();
  }

  onContactPhoneE164Change(e164: string | null): void {
    this.contactPhoneE164 = e164;
    this.isContactPhoneValid = Boolean(e164);
    if (e164 && this.trayError === 'Enter a valid US mobile number.') {
      this.trayError = null;
    }
  }

  copyCode(contact: TrustedContact) {
    this.copyText(contact.token, contact.id);
  }

  openDeleteTray(contact: TrustedContact) {
    if (contact.used) {
      return;
    }

    this.contactToDelete = contact;
    this.trayError = null;
  }

  closeDeleteTray() {
    if (this.isDeleting) {
      return;
    }

    this.contactToDelete = null;
  }

  confirmDeleteContact() {
    if (!this.contactToDelete || this.isDeleting) {
      return;
    }

    const contact = this.contactToDelete;
    this.isDeleting = true;
    this.trayError = null;

    this.inviteService.deleteTrustedContact(contact.id).subscribe({
      next: () => {
        this.contacts = this.contacts.filter(existing => existing.id !== contact.id);
        this.contactToDelete = null;
        this.isDeleting = false;
      },
      error: err => {
        this.trayError = err.error?.error || 'Failed to delete trusted contact.';
        this.isDeleting = false;
      }
    });
  }

  copyCreatedMessage() {
    if (!this.createdContact) {
      return;
    }

    this.copyText(this.createdContactMessage, `${this.createdContact.id}-message`);
  }

  formatPhone(phone: string): string {
    const digits = `${phone || ''}`.replace(/\D/g, '');
    const normalized = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;

    if (normalized.length !== 10) {
      return phone;
    }

    return `(${normalized.slice(0, 3)}) ${normalized.slice(3, 6)}-${normalized.slice(6)}`;
  }

  buildShareMessage(contact: TrustedContact): string {
    return `You're invited to join TailOne as my Trusted Contact. This invite is tied to ${this.formatPhone(contact.phone)} and can only be redeemed by you. Signup code: ${contact.token}`;
  }

  private resetTray() {
    this.trayState = 'form';
    this.contactName = '';
    this.contactPhone = '';
    this.contactPhoneE164 = null;
    this.isContactPhoneValid = false;
    this.trayError = null;
    this.createdContact = null;
    this.isSubmitting = false;
  }

  private isSoftVerifiedProfile(profile: any): boolean {
    if (!profile) {
      return false;
    }

    if (
      profile.softVerified === true ||
      profile.isSoftVerified === true ||
      profile.verification?.softVerified === true
    ) {
      return true;
    }

    const profileStatus = this.normalizeVerificationStatus(
      profile.verificationStatus ||
      profile.accountVerificationStatus ||
      profile.membershipVerificationStatus ||
      profile.verification?.status
    );

    if (this.isPendingVerificationStatus(profileStatus)) {
      return true;
    }

    const memberships = Array.isArray(profile.memberships) ? profile.memberships : [];
    return memberships.some((membership: any) => {
      if (membership?.softVerified === true || membership?.isSoftVerified === true) {
        return true;
      }

      const membershipStatus = this.normalizeVerificationStatus(
        membership?.statementStatus ||
        membership?.verificationStatus ||
        membership?.status
      );

      return this.isPendingVerificationStatus(membershipStatus);
    });
  }

  private normalizeVerificationStatus(status: any): string {
    return `${status || ''}`.trim().toLowerCase().replace(/[_\s]+/g, '-');
  }

  private isPendingVerificationStatus(status: string): boolean {
    return [
      'soft-verified',
      'softverified',
      'pending',
      'pending-review',
      'review',
      'manual-review',
      'under-review',
      'in-review',
      'needs-review',
      'awaiting-review'
    ].includes(status);
  }

  private normalizedContactPhone(): string | null {
    if (this.contactPhoneE164) {
      return this.contactPhoneE164;
    }

    const value = typeof this.contactPhone === 'object'
      ? this.contactPhone?.e164 || this.contactPhone?.rawDigits || ''
      : this.contactPhone || '';
    const digits = `${value}`.replace(/\D/g, '');

    if (digits.length === 10) {
      return `+1${digits}`;
    }

    if (digits.length === 11 && digits.startsWith('1')) {
      return `+${digits}`;
    }

    return null;
  }

  private async copyText(text: string, key: string) {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        this.copyWithFallback(text);
      }
      this.copiedKey = key;
      setTimeout(() => {
        if (this.copiedKey === key) {
          this.copiedKey = null;
        }
      }, 1600);
    } catch (err) {
      this.copyWithFallback(text);
      this.copiedKey = key;
    }
  }

  private copyWithFallback(text: string) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  }
}
